import {isRight} from 'fp-ts/lib/Either';
import * as t from 'io-ts';

import {FrontConfig} from '../../helpers/bundler/bundlerDefineConfig';
import {isPreview} from '../../helpers/environment/envHelpers';
import {isMobile} from '../../helpers/environment/mobileEnvHelpers';
import {findMobileBuildNumber} from '../../helpers/metadata/mobileBuildNumber';
import {makeOptional} from '../../models/api/types/optionalType';
import {reportToBugsnag} from '../../services/bugsnag/bugsnag';

const RVersionedFeatureFlag = t.type({
  web: makeOptional(t.number),
  mobile: makeOptional(t.number),
});

export type VersionedFeatureFlag = t.TypeOf<typeof RVersionedFeatureFlag>;

export const parseVersionedFeatureFlag = (flag: unknown): VersionedFeatureFlag => {
  const decoded = RVersionedFeatureFlag.decode(flag);
  if (isRight(decoded)) {
    return decoded.right;
  }

  return {
    web: undefined,
    mobile: undefined,
  };
};

export const isWebVersionedFeatureFlagEnabled = (flag: VersionedFeatureFlag): boolean => {
  const minTimestamp = flag.web;
  if (isMobile() || !minTimestamp) {
    return false;
  }

  if (!FrontConfig.FEATURE_FLAG_TIMESTAMP) {
    reportToBugsnag(new Error('Missing FrontConfig.FEATURE_FLAG_TIMESTAMP on web build'));
    return false;
  }

  return FrontConfig.FEATURE_FLAG_TIMESTAMP >= minTimestamp;
};

export const isMobileVersionedFeatureFlagEnabled = (flag: VersionedFeatureFlag): boolean => {
  const minBuildNumber = flag.mobile;
  if (!isMobile() || !minBuildNumber) {
    return false;
  }

  const buildNumber = findMobileBuildNumber();
  if (!buildNumber) {
    reportToBugsnag(new Error('Unable to lookup mobile build number'));
    return false;
  }

  return buildNumber >= minBuildNumber || isPreview();
};

export const isVersionedFeatureFlagEnabled = (flag: VersionedFeatureFlag): boolean =>
  isMobile() ? isMobileVersionedFeatureFlagEnabled(flag) : isWebVersionedFeatureFlagEnabled(flag);
